import { FC, useEffect, useRef, useState } from 'react';
import { useKey } from 'react-use';
import { BUTTON_SIZES, GhostButton, SecondaryButton } from 'ui/button';
import { useFocusTrap } from 'utils/hooks/useFocusTrap';
import noop from 'utils/noop';
import { MODAL_SIZES, MODAL_VARIANTS } from './Modal.constants';
import { ModalConfirmationFooterActionButton } from './ModalConfirmationFooter.ActionButton';
import { ModalConfirmationFooterPromptLabel } from './ModalConfirmationFooter.PromptLabel';
import {
  ActionsContainer,
  ButtonsGroup,
  Content,
  Overlay,
  PrimaryActions,
} from './ModalConfirmationFooter.styled';

import { FooterMessageContainer } from './Modal.styled';
import { ModalProvider } from './ModalContext';
import { ConfirmationPrompt, ModalConfirmationFooterProps } from './types';

const CONTENT = {
  cancelButtonLabel: 'Cancel',
  confirmButtonLabel: 'Confirm changes',
  confirmLabel: 'Confirm changes?',
  discardButtonLabel: 'Close & discard',
  discardLabel: 'Discard changes?',
  helpText: ' Closing this window will cancel any unsaved changes.',
  resumeButtonLabel: 'Keep editing',
};

/** ModalConfirmationFooter
 * A modal footer that shows a banner that requires a second confirmation before
 * the action taken in the footer is accepted.
 *
 * The state for which banner is visible needs to kept track of externally,
 * and is provided via the `promptType` prop to the ModalConfirmationFooter.
 *
 * A "simpler" version, which keeps track of which banner is visible internally,
 * can be found at SimpleModalConfirmationFooter
 */
export const ModalConfirmationFooter: FC<ModalConfirmationFooterProps> = ({
  animationTime = '0.25s',
  buttonSize = BUTTON_SIZES.MEDIUM,
  cancelButtonLabel = CONTENT.cancelButtonLabel,
  children,
  confirmButtonLabel = CONTENT.confirmButtonLabel,
  confirmHelpText = CONTENT.helpText,
  confirmLabel = CONTENT.confirmLabel,
  discardButtonLabel = CONTENT.discardButtonLabel,
  discardHelpText = CONTENT.helpText,
  discardLabel = CONTENT.discardLabel,
  id = 'modal-confirmation-banner',
  modalSize = MODAL_SIZES.MEDIUM,
  message = '',
  onCancel,
  onConfirm,
  onDiscard,
  onResume,
  promptType,
  resumeButtonLabel = CONTENT.resumeButtonLabel,
  variant = MODAL_VARIANTS.DEFAULT,
  ...props
}) => {
  const { CONFIRM, DISCARD } = ConfirmationPrompt;
  const ConfirmationBanner = useRef<HTMLDialogElement>(null);
  const isPromptEnabled = promptType === CONFIRM || promptType === DISCARD;
  const [showExitAnimation, setShowExitAnimation] = useState(false);
  // we cannot use the useFocusTrap to correctly fire off the functionality
  // we want when we're trying to "Esc" out of the pop-up banner.
  // sadly this is a necessary hack as we have to override
  // the useFocusTrap that's implemented in the base modal, because otherwise _both_ escape functions,
  // here and in the base modal, fire, and the entire modal closes when all we wanted is for the popup footer banner
  // to close instead.
  useKey(
    'Escape',
    (e) => {
      if (isPromptEnabled) {
        e.stopPropagation();
        if (promptType === CONFIRM) {
          onCancel?.();
        } else {
          onResume?.();
        }
      }
    },
    undefined,
    [promptType],
  );

  // we need to set up a focus trap, but don't need to provide a function (hence the noop)
  // to close out the trap via keyboard commands, as explained in the comment above.
  // the focus trap itself is useful though as we want the popup to be our new dialog element of choice
  // while it is active.
  useFocusTrap(noop, ConfirmationBanner, !isPromptEnabled, [promptType]);

  // for some reason, the focus stays outside of the popup banner when it is instantiated, and instead remains
  // in the base modal. so we manually force the focus to reassign itself to the banner popup when it is instantiated
  useEffect(() => {
    if (isPromptEnabled && ConfirmationBanner.current) {
      ConfirmationBanner.current.focus();
      setShowExitAnimation(true);
    }
  }, [promptType]);

  return (
    <>
      <Overlay data-test={`${id}-overlay`} isEnabled={isPromptEnabled} />
      <ModalProvider
        withPortal={false}
        size={modalSize}
        variant={variant}
        onClose={onDiscard || noop}
      >
        {(isPromptEnabled || showExitAnimation) && (
          <Content
            $animationTime={animationTime}
            data-test={`${id}-content`}
            isEnabled={isPromptEnabled}
            ref={ConfirmationBanner}
            size={modalSize}
            onAnimationEnd={() => !isPromptEnabled && setShowExitAnimation(false)}
          >
            <ModalConfirmationFooterPromptLabel
              confirmHelpText={confirmHelpText}
              confirmLabel={confirmLabel}
              discardHelpText={discardHelpText}
              discardLabel={discardLabel}
              promptType={promptType}
              showExitAnimation={showExitAnimation}
            />
            <ButtonsGroup>
              <SecondaryButton
                data-test={`${id}-resume-button`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onResume();
                }}
                size={buttonSize}
              >
                {resumeButtonLabel}
              </SecondaryButton>
              <ModalConfirmationFooterActionButton
                confirmButtonLabel={confirmButtonLabel}
                discardButtonLabel={discardButtonLabel}
                id={id}
                onConfirm={onConfirm || noop}
                onDiscard={onDiscard || noop}
                promptType={promptType}
                showExitAnimation={showExitAnimation}
                size={buttonSize}
              />
            </ButtonsGroup>
          </Content>
        )}
      </ModalProvider>

      <ActionsContainer
        data-test={id}
        id={id}
        data-has-message={!!message}
        size={modalSize}
        {...props}
      >
        {message && <FooterMessageContainer>{message}</FooterMessageContainer>}
        <PrimaryActions>
          {onCancel && (
            <GhostButton data-test="footer-cancel-button" onClick={onCancel}>
              {cancelButtonLabel}
            </GhostButton>
          )}
          {children}
        </PrimaryActions>
      </ActionsContainer>
    </>
  );
};
